import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { formatCurrency, formatDate, generateId } from '../../utils/helpers';
import { Printer, ArrowLeft } from 'lucide-react';

function FeeCollection() {
  const { feePayments, setFeePayments, feeStructures, students, classes, parents } = useApp();
  const { currentUser } = useAuth();
  const [selectedClass, setSelectedClass] = useState(classes[0]?.id || '');
  const [ledgerStudent, setLedgerStudent] = useState(null); // student whose ledger is open
  const [collectModal, setCollectModal] = useState(false);
  const [receiptPayment, setReceiptPayment] = useState(null); // payment to show receipt for
  const [form, setForm] = useState({ amount: '', paymentMode: 'cash', transactionRef: '', discount: 0, fine: 0 });

  const isParent = currentUser?.role === 'parent';
  const parentRecord = isParent ? parents?.find(p => p.id === currentUser?.profileId) : null;
  const childIds = parentRecord?.studentIds || [];
  const childStudents = isParent ? students.filter(s => childIds.includes(s.id)) : [];

  const getStudentClass = (s) => { const c = classes.find(c => c.id === s.classId); return c ? `${c.name}-${c.section}` : '-'; };
  const getStudentPayments = (studentId, type = 'current') =>
    feePayments.filter(p => p.studentId === studentId && (type === 'current' ? p.type !== 'prevBalance' : p.type === 'prevBalance'))
      .sort((a, b) => a.paidDate.localeCompare(b.paidDate));
  const getStudentFeeStatus = (student) => {
    const fs = feeStructures.find(f => f.classId === student.classId);
    if (!fs) return null;
    const prevBal = student.previousYearBalance || 0;
    const currentPaid = getStudentPayments(student.id, 'current').reduce((sum, p) => sum + Number(p.amount), 0);
    const prevPaid = getStudentPayments(student.id, 'prevBalance').reduce((sum, p) => sum + Number(p.amount), 0);
    const totalDue = fs.totalAmount + prevBal;
    const totalPaid = currentPaid + prevPaid;
    const status = totalPaid >= totalDue ? 'paid' : totalPaid > 0 ? 'partial' : 'pending';
    return { fs, totalDue, paid: totalPaid, remaining: totalDue - totalPaid, status };
  };

  const displayStudents = isParent
    ? childStudents
    : students.filter(s => s.status === 'active' && s.classId === selectedClass)
        .sort((a, b) => Number(a.rollNumber) - Number(b.rollNumber));

  const savePayment = () => {
    const fs = feeStructures.find(f => f.classId === ledgerStudent.classId);
    const prevBal = ledgerStudent.previousYearBalance || 0;
    const prevPaid = getStudentPayments(ledgerStudent.id, 'prevBalance').reduce((s, p) => s + Number(p.amount), 0);
    const isPrevBalancePending = prevBal > 0 && prevPaid < prevBal;
    const prev = feePayments.filter(p => p.studentId === ledgerStudent.id).reduce((s, p) => s + Number(p.amount), 0);
    const total = prev + Number(form.amount);
    const newPayment = {
      id: generateId('PAY'),
      studentId: ledgerStudent.id,
      feeStructureId: isPrevBalancePending ? 'PREV' : (fs?.id || ''),
      type: isPrevBalancePending ? 'prevBalance' : 'current',
      amount: Number(form.amount),
      discount: Number(form.discount) || 0,
      fine: Number(form.fine) || 0,
      paymentMode: form.paymentMode,
      transactionRef: form.transactionRef,
      paidDate: new Date().toISOString().split('T')[0],
      collectedBy: currentUser?.profileId || 'STF-001',
      status: total >= (fs?.totalAmount || 0) + prevBal ? 'paid' : 'partial',
    };
    setFeePayments(prev => [...prev, newPayment]);
    setCollectModal(false);
    setForm({ amount: '', paymentMode: 'cash', transactionRef: '', discount: 0, fine: 0 });
    setReceiptPayment({ ...newPayment, student: ledgerStudent, fs });
  };

  const printLedger = (student, fs, currentPayments, prevPayments, prevBal, totalDue, totalPaid, totalBalance) => {
    const cls = classes.find(c => c.id === student?.classId);
    const className = cls ? `${cls.name}-${cls.section}` : '';
    const components = fs?.components || [];
    const currentPaid = currentPayments.reduce((s, p) => s + Number(p.amount), 0);
    const prevPaid    = prevPayments.reduce((s, p) => s + Number(p.amount), 0);

    // Format: Fees | Status | Amount | Payment ID | Mode | Date | Paid | Balance
    const fmt = (n) => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

    const compRows = components.map((comp, idx) => {
      const isFirst = idx === 0;
      const compPaid = isFirst ? currentPaid : 0;
      const compBal  = isFirst ? (fs?.totalAmount || 0) - currentPaid : 0;
      const subRows = isFirst ? currentPayments.map((pay, pi) => {
        const paidSoFar = currentPayments.slice(0, pi + 1).reduce((s, p) => s + Number(p.amount), 0);
        const bal = (fs?.totalAmount || 0) - paidSoFar;
        return `<tr class="sub">
          <td style="padding-left:32px;color:#888">↳</td>
          <td class="num">${fmt(pay.amount)}</td>
          <td>${pay.id}</td>
          <td style="text-transform:capitalize">${pay.paymentMode}</td>
          <td>${formatDate(pay.paidDate)}</td>
          <td class="num">${fmt(pay.amount)}</td>
          <td class="num" style="color:${bal>0?'#dc2626':'#16a34a'}">${fmt(bal)}</td>
        </tr>`;
      }).join('') : '';

      return `<tr class="head">
        <td>${comp.name} <span style="color:#888;font-weight:400;font-size:11px">(${comp.name.replace(/\s/g,'')})</span></td>
        <td class="num">${fmt(comp.amount)}</td>
        <td></td><td></td><td></td>
        <td class="num">${isFirst ? fmt(compPaid) : ''}</td>
        <td class="num" style="color:${compBal>0?'#dc2626':'#16a34a'}">${isFirst ? fmt(compBal) : ''}</td>
      </tr>${subRows}`;
    }).join('');

    const prevRow = prevBal > 0 ? (() => {
      const subRows = prevPayments.map((pay, pi) => {
        const paidSoFar = currentPaid + prevPayments.slice(0, pi+1).reduce((s,p)=>s+Number(p.amount),0);
        const bal = totalDue - paidSoFar;
        return `<tr class="sub prev-sub">
          <td style="padding-left:32px;color:#888">↳</td>
          <td class="num">${fmt(pay.amount)}</td>
          <td>${pay.id}</td>
          <td style="text-transform:capitalize">${pay.paymentMode}</td>
          <td>${formatDate(pay.paidDate)}</td>
          <td class="num">${fmt(pay.amount)}</td>
          <td class="num" style="color:${bal>0?'#dc2626':'#16a34a'}">${fmt(bal)}</td>
        </tr>`;
      }).join('');
      return `<tr class="head prev-head">
        <td style="color:#c2410c;font-weight:600">Previous Year Balance <span style="color:#888;font-weight:400;font-size:11px">(PreviousYearBalance)</span></td>
        <td class="num" style="color:#c2410c">${fmt(prevBal)}</td>
        <td></td><td></td><td></td>
        <td class="num">${fmt(prevPaid)}</td>
        <td class="num" style="color:${prevBal-prevPaid>0?'#dc2626':'#16a34a'}">${fmt(prevBal-prevPaid)}</td>
      </tr>${subRows}`;
    })() : '';

    const win = window.open('', '_blank', 'width=860,height=700');
    win.document.open(); win.document.write(`
      <html><head><title>Fee Ledger - ${student?.firstName} ${student?.lastName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Roboto', Arial, sans-serif; padding: 28px 32px; color: #111; font-size: 13px; }
        .school-header { text-align:center; padding-bottom:10px; margin-bottom:16px; border-bottom:2px solid #1e3a8a; }
        .school-header h2 { color:#1e3a8a; font-size:20px; margin-bottom:2px; }
        .school-header p { font-size:12px; color:#555; }
        .meta { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; padding-bottom:12px; border-bottom:1px solid #e5e7eb; }
        .meta-left .name { font-size:16px; font-weight:700; color:#1e3a8a; }
        .meta-left .details { font-size:12px; color:#555; margin-top:3px; }
        .meta-right { text-align:right; font-size:12px; color:#555; }
        .meta-right .total { font-size:14px; font-weight:700; color:#111; }
        table { width:100%; border-collapse:collapse; }
        thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:12px; padding:8px 10px; text-align:left; border-bottom:2px solid #d1d5db; }
        thead th.num { text-align:right; }
        td { padding:7px 10px; border-bottom:1px solid #f3f4f6; vertical-align:middle; font-size:12px; }
        tr.head td { font-weight:600; background:#fff; }
        tr.sub td { color:#111; background:#fafafa; }
        tr.prev-head td { background:#fff7ed; }
        tr.prev-sub td { background:#fff7ed; opacity:0.85; }
        tr.total td { background:#f1f5f9; font-weight:700; border-top:2px solid #d1d5db; border-bottom:none; font-size:13px; }
        .num { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
        .badge-paid { background:#16a34a; color:#fff; padding:1px 9px; border-radius:20px; font-size:11px; font-weight:600; }
        .badge-partial { background:#eab308; color:#fff; padding:1px 9px; border-radius:20px; font-size:11px; font-weight:600; }
        .badge-pending { background:#e5e7eb; color:#374151; padding:1px 9px; border-radius:20px; font-size:11px; font-weight:600; }
        .footer { text-align:center; font-size:11px; color:#aaa; margin-top:24px; padding-top:8px; border-top:1px dashed #e5e7eb; }
        .print-btn { text-align:center; margin-top:18px; }
        @media print { .print-btn { display:none; } body { padding:16px; } }
      </style></head><body>
      <div class="school-header">
        <h2>Vasavya Vidyalayam</h2>
        <p>Fee Ledger &mdash; Academic Year ${fs?.academicYear || '2025-2026'}</p>
      </div>
      <div class="meta">
        <div class="meta-left">
          <div class="name">${student?.firstName} ${student?.lastName}</div>
          <div class="details">Class: ${className} &nbsp;&bull;&nbsp; Roll No: ${student?.rollNumber} &nbsp;&bull;&nbsp; Adm No: ${student?.admissionNumber || '—'}</div>
        </div>
        <div class="meta-right">
          <div class="total">Total Due: ${fmt(totalDue)}</div>
          <div>Paid: ${fmt(totalPaid)} &nbsp;&bull;&nbsp; Balance: <span style="color:${totalBalance>0?'#dc2626':'#16a34a'}">${fmt(totalBalance)}</span></div>
        </div>
      </div>
      <table>
        <thead><tr>
          <th>Fees</th>
          <th class="num">Amount</th>
          <th>Payment ID</th>
          <th>Mode</th>
          <th>Date</th>
          <th class="num">Paid (₹)</th>
          <th class="num">Balance (₹)</th>
        </tr></thead>
        <tbody>
          ${compRows}
          ${prevRow}
          <tr class="total">
            <td>Grand Total</td>
            <td class="num">${fmt(totalDue)}</td>
            <td colspan="3"></td>
            <td class="num" style="color:#16a34a">${fmt(totalPaid)}</td>
            <td class="num" style="color:${totalBalance>0?'#dc2626':'#16a34a'}">${fmt(totalBalance)}</td>
          </tr>
        </tbody>
      </table>
      <div class="footer">Computer-generated document &mdash; Vasavya Vidyalayam</div>
      <div class="print-btn">
        <button onclick="window.print()" style="background:#1e3a8a;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:14px;cursor:pointer">🖨 Print</button>
      </div>
      </body></html>
    `);
    win.document.close();
  };

  const printReceipt = (pay) => {
    const student = pay.student || ledgerStudent;
    const fs = pay.fs || feeStructures.find(f => f.classId === student?.classId);
    const cls = classes.find(c => c.id === student?.classId);
    const className = cls ? `${cls.name}-${cls.section}` : '';
    const fmt = (n) => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

    // All payments for this student
    const allCurrentPays = getStudentPayments(student?.id, 'current');
    const allPrevPays    = getStudentPayments(student?.id, 'prevBalance');
    const prevBal        = student?.previousYearBalance || 0;
    const totalDue       = (fs?.totalAmount || 0) + prevBal;
    const totalPaid      = [...allCurrentPays, ...allPrevPays].reduce((s, p) => s + Number(p.amount), 0);
    const totalBalance   = totalDue - totalPaid;

    // Build payment history rows with running balance
    const historyRows = [...allCurrentPays, ...allPrevPays]
      .sort((a, b) => a.paidDate.localeCompare(b.paidDate))
      .map((p, i, arr) => {
        const paidSoFar = arr.slice(0, i + 1).reduce((s, x) => s + Number(x.amount), 0);
        const bal = totalDue - paidSoFar;
        const isCurrent = p.id === pay.id;
        return `<tr style="${isCurrent ? 'background:#fffbeb;' : ''}">
          <td>${formatDate(p.paidDate)}</td>
          <td>${p.id}</td>
          <td style="text-transform:capitalize">${p.paymentMode}</td>
          ${p.transactionRef ? `<td>${p.transactionRef}</td>` : '<td>—</td>'}
          <td class="num">${fmt(p.amount)}</td>
          <td class="num" style="color:${bal > 0 ? '#dc2626' : '#16a34a'}">${fmt(bal)}</td>
          <td>${isCurrent ? '<span class="badge-current">Current</span>' : ''}</td>
        </tr>`;
      }).join('');

    const win = window.open('', '_blank', 'width=860,height=700');
    win.document.open(); win.document.write(`
      <html><head><title>Fee Receipt - ${student?.firstName} ${student?.lastName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Roboto', Arial, sans-serif; padding: 28px 32px; color: #111; font-size: 13px; }
        .school-header { text-align:center; padding-bottom:10px; margin-bottom:16px; border-bottom:2px solid #1e3a8a; }
        .school-header h2 { color:#1e3a8a; font-size:20px; margin-bottom:2px; }
        .school-header p { font-size:12px; color:#555; }
        .meta { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:16px; padding-bottom:12px; border-bottom:1px solid #e5e7eb; }
        .meta-left .name { font-size:16px; font-weight:700; color:#1e3a8a; }
        .meta-left .details { font-size:12px; color:#555; margin-top:3px; }
        .meta-right { text-align:right; font-size:12px; color:#555; }
        .meta-right .total { font-size:14px; font-weight:700; color:#111; }
        .summary { display:flex; gap:24px; margin-bottom:16px; padding:12px 16px; background:#f8fafc; border-radius:8px; border:1px solid #e5e7eb; }
        .summary-item { flex:1; }
        .summary-item .lbl { font-size:11px; color:#888; margin-bottom:2px; }
        .summary-item .val { font-size:14px; font-weight:700; color:#111; }
        .section-title { font-size:13px; font-weight:600; color:#1e3a8a; margin:16px 0 8px; padding-bottom:4px; border-bottom:1px solid #e5e7eb; }
        table { width:100%; border-collapse:collapse; }
        thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:12px; padding:8px 10px; text-align:left; border-bottom:2px solid #d1d5db; }
        thead th.num { text-align:right; }
        td { padding:7px 10px; border-bottom:1px solid #f3f4f6; vertical-align:middle; font-size:12px; }
        tr.total-row td { background:#f1f5f9; font-weight:700; border-top:2px solid #d1d5db; border-bottom:none; font-size:13px; }
        .num { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
        .badge { display:inline-block; background:#16a34a; color:#fff; padding:1px 9px; border-radius:20px; font-size:11px; font-weight:600; }
        .badge-current { display:inline-block; background:#1e3a8a; color:#fff; padding:1px 9px; border-radius:20px; font-size:11px; font-weight:600; }
        .footer { text-align:center; font-size:11px; color:#aaa; margin-top:24px; padding-top:8px; border-top:1px dashed #e5e7eb; }
        .print-btn { text-align:center; margin-top:18px; }
        @media print { .print-btn { display:none; } body { padding:16px; } }
      </style></head><body>
      <div class="school-header">
        <h2>Vasavya Vidyalayam</h2>
        <p>Fee Receipt &mdash; Academic Year ${fs?.academicYear || '2025-2026'}</p>
      </div>
      <div class="meta">
        <div class="meta-left">
          <div class="name">${student?.firstName} ${student?.lastName}</div>
          <div class="details">Class: ${className} &nbsp;&bull;&nbsp; Roll No: ${student?.rollNumber} &nbsp;&bull;&nbsp; Adm No: ${student?.admissionNumber || '—'}</div>
        </div>
        <div class="meta-right">
          <div class="total">Receipt No: ${pay.id}</div>
          <div>Date: ${formatDate(pay.paidDate)}</div>
          <div>Mode: <strong style="text-transform:capitalize">${pay.paymentMode}</strong></div>
        </div>
      </div>

      <div class="summary">
        <div class="summary-item">
          <div class="lbl">Total Fee Due</div>
          <div class="val">${fmt(totalDue)}</div>
        </div>
        <div class="summary-item">
          <div class="lbl">Total Paid</div>
          <div class="val" style="color:#16a34a">${fmt(totalPaid)}</div>
        </div>
        <div class="summary-item">
          <div class="lbl">Balance</div>
          <div class="val" style="color:${totalBalance > 0 ? '#dc2626' : '#16a34a'}">${fmt(totalBalance)}</div>
        </div>
        <div class="summary-item">
          <div class="lbl">This Payment</div>
          <div class="val" style="color:#1e3a8a">${fmt(pay.amount)}</div>
        </div>
      </div>

      <div class="section-title">Payment History</div>
      <table>
        <thead><tr>
          <th>Date</th>
          <th>Receipt No</th>
          <th>Mode</th>
          <th>Reference</th>
          <th class="num">Amount (₹)</th>
          <th class="num">Balance (₹)</th>
          <th></th>
        </tr></thead>
        <tbody>
          ${historyRows}
        </tbody>
        <tfoot>
          <tr class="total-row">
            <td colspan="4">Grand Total</td>
            <td class="num" style="color:#16a34a">${fmt(totalPaid)}</td>
            <td class="num" style="color:${totalBalance > 0 ? '#dc2626' : '#16a34a'}">${fmt(totalBalance)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      <div class="footer">Computer-generated receipt &mdash; Vasavya Vidyalayam</div>
      <div class="print-btn">
        <button onclick="window.print()" style="background:#1e3a8a;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:14px;cursor:pointer;font-family:'Roboto',Arial,sans-serif">🖨 Print</button>
      </div>
      </body></html>
    `);
    win.document.close();
  };

  // ── Parent view ──────────────────────────────────────────────────────────────
  if (isParent) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Fee Receipts</h1>
          <p className="text-gray-500 text-sm">Your child's fee details and payment history</p>
        </div>
        {childStudents.map(student => {
          const feeStatus = getStudentFeeStatus(student);
          const receipts = getStudentPayments(student.id);
          return (
            <div key={student.id} className="mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-bold text-lg">{student.firstName} {student.lastName}</h2>
                    <p className="text-sm">{getStudentClass(student)} &middot; Roll No: {student.rollNumber}</p>
                  </div>
                  {feeStatus && <Badge value={feeStatus.status} />}
                </div>
                {feeStatus ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="mb-1">Total Fee</p>
                      <p className="text-lg font-bold">{formatCurrency(feeStatus.totalDue)}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="mb-1">Paid</p>
                      <p className="text-lg font-bold text-green-600">{formatCurrency(feeStatus.paid)}</p>
                    </div>
                    <div className={`rounded-lg p-3 text-center ${feeStatus.remaining > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                      <p className="mb-1">Balance Due</p>
                      <p className={`text-lg font-bold ${feeStatus.remaining > 0 ? 'text-red-500' : 'text-green-600'}`}>{formatCurrency(feeStatus.remaining)}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm">No fee structure defined for this class.</p>
                )}
              </div>
              <h3 className="text-sm font-semibold mb-2 px-1">Payment History</h3>
              {receipts.length === 0 ? (
                <div className="bg-white rounded-xl p-6 text-center text-sm border border-gray-100">No payments recorded yet</div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="px-4 py-1 text-left font-bold">Receipt No</th>
                        <th className="px-4 py-1 text-left font-bold">Date</th>
                        <th className="px-4 py-1 text-left font-bold">Mode</th>
                        <th className="px-4 py-1 text-left font-bold">Reference</th>
                        <th className="px-4 py-1 text-right font-bold">Amount</th>
                        <th className="px-4 py-1 text-center font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {receipts.map(r => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-4 py-1 font-mono">{r.id}</td>
                          <td className="px-4 py-1">{formatDate(r.paidDate)}</td>
                          <td className="px-4 py-1 capitalize">{r.paymentMode}</td>
                          <td className="px-4 py-1">{r.transactionRef || '—'}</td>
                          <td className="px-4 py-1 text-right font-semibold text-green-600">{formatCurrency(r.amount)}</td>
                          <td className="px-4 py-1 text-center"><Badge value={r.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Student Fee Ledger (after clicking Collect) ───────────────────────────────
  if (ledgerStudent) {
    const fs = feeStructures.find(f => f.classId === ledgerStudent.classId);
    const prevBal = ledgerStudent.previousYearBalance || 0;

    const currentPayments = getStudentPayments(ledgerStudent.id, 'current');
    const prevPayments    = getStudentPayments(ledgerStudent.id, 'prevBalance');

    const currentPaid    = currentPayments.reduce((s, p) => s + Number(p.amount), 0);
    const prevPaid       = prevPayments.reduce((s, p) => s + Number(p.amount), 0);
    const totalDiscount  = [...currentPayments, ...prevPayments].reduce((s, p) => s + Number(p.discount || 0), 0);
    const totalFine      = [...currentPayments, ...prevPayments].reduce((s, p) => s + Number(p.fine || 0), 0);
    const totalDue       = (fs?.totalAmount || 0) + prevBal;
    const totalPaid      = currentPaid + prevPaid;
    const totalBalance   = totalDue - totalPaid;
    const overallStatus  = totalPaid >= totalDue ? 'paid' : totalPaid > 0 ? 'partial' : 'pending';

    const components = fs?.components || [];

    const renderPaySubRows = (pays, runningStart) => pays.map((pay, pi) => {
      const paidSoFar = runningStart + pays.slice(0, pi + 1).reduce((s, p) => s + Number(p.amount), 0);
      const balanceAfter = totalDue - paidSoFar;
      return (
        <tr key={pay.id} className="border-b border-gray-50 bg-gray-50/50">
          <td className="px-3 py-1"></td>
          <td className="px-3 py-1 pl-8">↳</td>
          <td className="px-3 py-1">—</td>
          <td className="px-3 py-1 text-center">—</td>
          <td className="px-3 py-1 text-right">—</td>
          <td className="px-3 py-1 max-w-[120px] truncate" title={pay.id}>{pay.id}</td>
          <td className="px-3 py-1 capitalize">{pay.paymentMode}</td>
          <td className="px-3 py-1">{formatDate(pay.paidDate)}</td>
          <td className="px-3 py-1 text-right" style={{ color: (pay.discount || 0) > 0 ? '#2563eb' : '#111827' }}>{formatCurrency(pay.discount || 0)}</td>
          <td className="px-3 py-1 text-right">{formatCurrency(pay.fine || 0)}</td>
          <td className="px-3 py-1 text-right font-medium">{formatCurrency(pay.amount)}</td>
          <td className="px-3 py-1 text-right font-medium" style={{ color: balanceAfter > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(balanceAfter)}</td>
          <td className="px-3 py-1 text-center"><button onClick={() => printReceipt({ ...pay, student: ledgerStudent, fs })} className="text-gray-400 hover:text-gray-700"><Printer size={13} /></button></td>
        </tr>
      );
    });

    return (
      <div>
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => setLedgerStudent(null)} className="flex items-center gap-1.5 text-sm hover:text-gray-800">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="h-4 w-px bg-gray-300" />
          <div>
            <h1 className="text-xl font-bold">{ledgerStudent.firstName} {ledgerStudent.lastName}</h1>
            <p className="">{getStudentClass(ledgerStudent)} &middot; Roll No: {ledgerStudent.rollNumber}</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full font-semibold ${overallStatus === 'paid' ? 'bg-green-100 text-green-700' : overallStatus === 'partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}`}>
              {overallStatus.charAt(0).toUpperCase() + overallStatus.slice(1)}
            </span>
            <button onClick={() => printLedger(ledgerStudent, fs, currentPayments, prevPayments, prevBal, totalDue, totalPaid, totalBalance)}
              className="flex items-center gap-1.5 px-3 py-1 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50">
              <Printer size={14} /> Print Ledger
            </button>
            {overallStatus !== 'paid' && (
              <button onClick={() => { setForm({ amount: String(totalBalance), paymentMode: 'cash', transactionRef: '', discount: 0, fine: 0 }); setCollectModal(true); }}
                className="px-4 py-1 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: '#1e3a8a' }}>
                + Collect Payment
              </button>
            )}
          </div>
        </div>

        {/* Ledger Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="px-3 py-1 text-left font-bold w-6"></th>
                <th className="px-3 py-1 text-left font-bold">Fees</th>
                <th className="px-3 py-1 text-left font-bold">Due Date</th>
                <th className="px-3 py-1 text-center font-bold">Status</th>
                <th className="px-3 py-1 text-right font-bold">Amount (₹)</th>
                <th className="px-3 py-1 text-left font-bold">Payment ID</th>
                <th className="px-3 py-1 text-left font-bold">Mode</th>
                <th className="px-3 py-1 text-left font-bold">Date</th>
                <th className="px-3 py-1 text-right font-bold">Discount (₹)</th>
                <th className="px-3 py-1 text-right font-bold">Fine (₹)</th>
                <th className="px-3 py-1 text-right font-bold">Paid (₹)</th>
                <th className="px-3 py-1 text-right font-bold">Balance (₹)</th>
                <th className="px-3 py-1 text-center font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {components.map((comp, idx) => {
                const isFirst = idx === 0;
                // Show current-year payments only under first component
                const compPaid = isFirst ? currentPaid : 0;
                const compBalance = isFirst ? (fs?.totalAmount || 0) - currentPaid : 0;
                const compStatus = isFirst ? (currentPaid >= (fs?.totalAmount || 0) ? 'paid' : currentPaid > 0 ? 'partial' : 'pending') : (comp.amount === 0 ? 'paid' : 'pending');
                return (
                  <React.Fragment key={idx}>
                    {/* Component header row */}
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-1">
                        <input type="checkbox" className="rounded" />
                      </td>
                      <td className="px-3 py-1 font-semibold">
                        {comp.name} <span className="text-gray-400 font-normal">({comp.name.replace(/\s/g, '')})</span>
                      </td>
                      <td className="px-3 py-1">{fs?.dueDate ? formatDate(fs.dueDate) : '—'}</td>
                      <td className="px-3 py-1 text-center">
                        <span className={`px-2 py-0.5 rounded font-semibold ${compStatus === 'paid' ? 'bg-green-500 text-white' : compStatus === 'partial' ? 'bg-yellow-400 text-white' : 'bg-gray-200'}`}>
                          {compStatus.charAt(0).toUpperCase() + compStatus.slice(1)}
                        </span>
                      </td>
                      <td className="px-3 py-1 text-right font-medium">{formatCurrency(comp.amount)}</td>
                      <td className="px-3 py-1">—</td>
                      <td className="px-3 py-1">—</td>
                      <td className="px-3 py-1">—</td>
                      <td className="px-3 py-1 text-right">{isFirst ? formatCurrency(currentPayments.reduce((s,p)=>s+(p.discount||0),0)) : '0.00'}</td>
                      <td className="px-3 py-1 text-right">{isFirst ? formatCurrency(currentPayments.reduce((s,p)=>s+(p.fine||0),0)) : '0.00'}</td>
                      <td className="px-3 py-1 text-right font-medium">{formatCurrency(compPaid)}</td>
                      <td className="px-3 py-1 text-right font-medium" style={{ color: compBalance > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(compBalance)}</td>
                      <td className="px-3 py-1 text-center">
                        <button className="text-gray-400 hover:text-gray-700" title="Print"><Printer size={14} /></button>
                      </td>
                    </tr>
                    {/* Payment sub-rows — only under first component */}
                    {isFirst && renderPaySubRows(currentPayments, 0)}
                  </React.Fragment>
                );
              })}

              {/* Previous Year Balance row */}
              {prevBal > 0 && (
                <React.Fragment>
                  <tr className="border-b border-gray-100 hover:bg-orange-50/30 bg-orange-50/20">
                    <td className="px-3 py-1"><input type="checkbox" className="rounded" /></td>
                    <td className="px-3 py-1 font-semibold text-orange-700">
                      Previous Year Balance <span className="text-gray-400 font-normal">(PreviousYearBalance)</span>
                    </td>
                    <td className="px-3 py-1">Carried forward</td>
                    <td className="px-3 py-1 text-center">
                      <span className={`px-2 py-0.5 rounded font-semibold ${prevPaid >= prevBal ? 'bg-green-500 text-white' : prevPaid > 0 ? 'bg-yellow-400 text-white' : 'bg-red-100 text-red-600'}`}>
                        {prevPaid >= prevBal ? 'Paid' : prevPaid > 0 ? 'Partial' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-3 py-1 text-right text-orange-700 font-medium">{formatCurrency(prevBal)}</td>
                    <td className="px-3 py-1">—</td>
                    <td className="px-3 py-1">—</td>
                    <td className="px-3 py-1">—</td>
                    <td className="px-3 py-1 text-right">0.00</td>
                    <td className="px-3 py-1 text-right">0.00</td>
                    <td className="px-3 py-1 text-right font-medium">{formatCurrency(prevPaid)}</td>
                    <td className="px-3 py-1 text-right font-medium" style={{ color: prevBal - prevPaid > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(prevBal - prevPaid)}</td>
                    <td className="px-3 py-1 text-center"><button className="text-gray-400 hover:text-gray-700"><Printer size={14} /></button></td>
                  </tr>
                  {renderPaySubRows(prevPayments, currentPaid)}
                </React.Fragment>
              )}

              {/* Grand Total */}
              <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                <td className="px-3 py-1" colSpan={2}></td>
                <td className="px-3 py-1 text-sm" colSpan={2}>Grand Total</td>
                <td className="px-3 py-1 text-right">
                  {formatCurrency(totalDue)}
                  {totalFine > 0 && <span className="text-red-500 ml-1">+ {formatCurrency(totalFine)}</span>}
                </td>
                <td colSpan={3}></td>
                <td className="px-3 py-1 text-right">₹{totalDiscount.toFixed(2)}</td>
                <td className="px-3 py-1 text-right">₹{totalFine.toFixed(2)}</td>
                <td className="px-3 py-1 text-right text-green-700">₹{totalPaid.toFixed(2)}</td>
                <td className="px-3 py-1 text-right" style={{ color: totalBalance > 0 ? '#dc2626' : '#16a34a' }}>₹{totalBalance.toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Collect Payment Modal */}
        {/* Receipt success modal */}
        <Modal isOpen={!!receiptPayment} onClose={() => setReceiptPayment(null)} title="Payment Collected">
          {receiptPayment && (
            <div className="text-center py-2">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <p className="text-lg font-bold mb-1">₹{Number(receiptPayment.amount).toLocaleString('en-IN')} Collected</p>
              <p className="text-sm mb-1">{receiptPayment.student?.firstName} {receiptPayment.student?.lastName}</p>
              <p className="mb-4">Receipt No: {receiptPayment.id} &middot; {formatDate(receiptPayment.paidDate)} &middot; <span className="capitalize">{receiptPayment.paymentMode}</span></p>
              <div className="flex justify-center gap-3">
                <button onClick={() => setReceiptPayment(null)} className="px-4 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
                <button onClick={() => { printReceipt(receiptPayment); setReceiptPayment(null); }}
                  className="flex items-center gap-2 px-4 py-1 text-sm text-white rounded-lg" style={{ backgroundColor: '#1e3a8a' }}>
                  <Printer size={14} /> Print Receipt
                </button>
              </div>
            </div>
          )}
        </Modal>

        <Modal isOpen={collectModal} onClose={() => setCollectModal(false)} title="Collect Payment">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-medium mb-1">Amount (₹)</label>
                <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
              <div>
                <label className="block font-medium mb-1">Payment Mode</label>
                <select value={form.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value }))}
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                  {['cash', 'cheque', 'online', 'UPI', 'DD'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block font-medium mb-1">Transaction Reference / Cheque No.</label>
              <input value={form.transactionRef} onChange={e => setForm(f => ({ ...f, transactionRef: e.target.value }))}
                className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="Optional" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-medium mb-1">Discount (₹)</label>
                <input type="number" value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))}
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
              <div>
                <label className="block font-medium mb-1">Fine (₹)</label>
                <input type="number" value={form.fine} onChange={e => setForm(f => ({ ...f, fine: e.target.value }))}
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <button onClick={() => setCollectModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
            <button onClick={savePayment} disabled={!form.amount}
              className="px-4 py-1 text-sm text-white rounded-lg hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#16a34a' }}>
              Collect Payment
            </button>
          </div>
        </Modal>
      </div>
    );
  }

  // ── Admin student list view ───────────────────────────────────────────────────
  const selectedClassObj = classes.find(c => c.id === selectedClass);
  const paidCount    = displayStudents.filter(s => getStudentFeeStatus(s)?.status === 'paid').length;
  const pendingCount = displayStudents.filter(s => getStudentFeeStatus(s)?.status !== 'paid').length;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Fee Collection</h1>
        <p className="text-gray-500 text-sm">Select a class to view and collect student fees</p>
      </div>

      {/* Class selector bar */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 mb-5">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="text-sm font-semibold">Select Class</label>
          <div className="flex flex-wrap gap-2">
            {classes.map(c => (
              <button key={c.id} onClick={() => setSelectedClass(c.id)}
                className={`px-4 py-1 rounded-lg text-sm font-medium border transition-colors ${selectedClass === c.id ? 'text-white border-transparent' : 'bg-white border-gray-300 hover:bg-gray-50'}`}
                style={selectedClass === c.id ? { backgroundColor: '#1e3a8a' } : {}}>
                {c.name} – {c.section}
              </button>
            ))}
          </div>
          {selectedClassObj && (
            <div className="ml-auto flex gap-3">
              <span className="bg-gray-100 px-2.5 py-1 rounded-lg font-medium">{displayStudents.length} students</span>
              <span className="bg-green-50 text-green-700 px-2.5 py-1 rounded-lg font-medium">{paidCount} paid</span>
              <span className="bg-red-50 text-red-600 px-2.5 py-1 rounded-lg font-medium">{pendingCount} pending</span>
            </div>
          )}
        </div>
      </div>

      {/* Students table */}
      {displayStudents.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">No active students in this class</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-1 text-left font-bold">Roll</th>
                <th className="px-4 py-1 text-left font-bold">Student</th>
                <th className="px-4 py-1 text-right font-bold">Total Fee</th>
                <th className="px-4 py-1 text-right font-bold">Paid</th>
                <th className="px-4 py-1 text-right font-bold">Balance</th>
                <th className="px-4 py-1 text-center font-bold">Status</th>
                <th className="px-4 py-1 text-center font-bold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {displayStudents.map(student => {
                const feeStatus = getStudentFeeStatus(student);
                if (!feeStatus) return (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-4 py-1">{student.rollNumber}</td>
                    <td className="px-4 py-1 font-medium">{student.firstName} {student.lastName}</td>
                    <td colSpan={5} className="px-4 py-1 text-center">No fee structure defined</td>
                  </tr>
                );
                return (
                  <tr key={student.id} className={`hover:bg-gray-50 cursor-pointer ${feeStatus.status === 'paid' ? 'bg-green-50/40' : ''}`}
                    onClick={() => setLedgerStudent(student)}>
                    <td className="px-4 py-1">{student.rollNumber}</td>
                    <td className="px-4 py-1 font-medium">{student.firstName} {student.lastName}</td>
                    <td className="px-4 py-1 text-right">{formatCurrency(feeStatus.totalDue)}</td>
                    <td className="px-4 py-1 text-right text-green-600 font-medium">{formatCurrency(feeStatus.paid)}</td>
                    <td className="px-4 py-1 text-right font-medium" style={{ color: feeStatus.remaining > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(feeStatus.remaining)}</td>
                    <td className="px-4 py-1 text-center"><Badge value={feeStatus.status} /></td>
                    <td className="px-4 py-1 text-center">
                      <button onClick={e => { e.stopPropagation(); setLedgerStudent(student); }}
                        className="font-medium text-white px-3 py-1.5 rounded-lg"
                        style={{ backgroundColor: feeStatus.status === 'paid' ? '#6b7280' : '#1e3a8a' }}>
                        {feeStatus.status === 'paid' ? 'View' : 'Collect'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
export default FeeCollection;
